import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import marielaSignature from "@/assets/mariela_signature.png";
import {
  IconArrowLeft, IconClock, IconCheck, IconVideo, IconBook, IconTools, IconBolt,
  IconLock, IconShoppingCart, IconLoader2, IconExternalLink, IconDownload, IconShieldLock,
  IconAward,
} from "@tabler/icons-react";
import { api, formatApiError, API } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const TYPE_ICON = {
  video: <IconVideo size={14} />,
  reading: <IconBook size={14} />,
  practice: <IconTools size={14} />,
  capsule: <IconBolt size={14} />,
};

// Convert URLs to embeddable iframe sources for major video hosts.
// Soporta YouTube, Vimeo, Google Drive, Dropbox, OneDrive y URLs directas (mp4/mov).
// Todos los embeds están configurados para minimizar chrome y ocultar controles de descarga cuando es posible.
function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // YouTube: modestbranding + rel=0 (sin sugerencias) + iv_load_policy=3 (sin anotaciones)
    if (u.hostname.endsWith("youtube.com") || u.hostname === "www.youtube.com") {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?modestbranding=1&rel=0&iv_load_policy=3`;
    }
    if (u.hostname === "youtu.be") {
      return `https://www.youtube.com/embed${u.pathname}?modestbranding=1&rel=0&iv_load_policy=3`;
    }
    // Vimeo: sin título/byline/portrait para chrome mínimo
    if (u.hostname.includes("vimeo.com")) {
      return `https://player.vimeo.com/video${u.pathname}?title=0&byline=0&portrait=0&pip=0`;
    }
    // Google Drive: /file/d/{ID}/view → /file/d/{ID}/preview
    // Nota: Drive SIEMPRE muestra un ícono "pop-out" para abrir en Drive — es limitación de Google.
    // Para videos privados es preferible subir el .mp4 al almacenamiento interno.
    if (u.hostname.includes("drive.google.com")) {
      const m = u.pathname.match(/\/file\/d\/([^/]+)/);
      if (m) return `https://drive.google.com/file/d/${m[1]}/preview`;
      if (u.pathname.endsWith("/preview")) return url;
    }
    // Dropbox: ?dl=0 → ?raw=1 (fuerza streaming inline)
    if (u.hostname.includes("dropbox.com")) {
      u.searchParams.set("raw", "1");
      u.searchParams.delete("dl");
      return u.toString();
    }
    // OneDrive share links con "?embed=1"
    if (u.hostname.includes("onedrive.live.com") || u.hostname.includes("1drv.ms")) {
      u.searchParams.set("embed", "1");
      return u.toString();
    }
  } catch { /* ignore parse errors */ }
  return url;
}

// Detecta URLs de archivos de video directos (mp4/mov/webm) — para elegir <video> vs <iframe>
function isDirectVideoUrl(url) {
  if (!url) return false;
  return /\.(mp4|mov|webm|m4v|ogv)($|\?)/i.test(url);
}

// Detecta URLs de Google Drive (para overlay que oculta el pop-out icon)
function isDriveEmbed(url) {
  if (!url) return false;
  try { return new URL(url).hostname.includes("drive.google.com"); } catch { return false; }
}

// Props para <video> HTML5:
// - controls: barra nativa siempre visible.
// - playsInline + webkitPlaysInline: crítico para iOS (Safari + PWA) — sin esto
//   iOS abre el video en el reproductor de OS que rompe la interfaz y muestra pantalla negra.
// - preload="metadata": iOS solo prepara metadata; evita bloqueo por carga completa.
// - controlsList: bloqueo de descarga sin romper reproducción.
// - crossOrigin="anonymous": necesario cuando el backend usa cookies para servir el archivo.
// NOTA: NO seteamos disablePictureInPicture=true porque en iOS bloquea la reproducción in-line.
const PROTECTED_VIDEO_PROPS = {
  controls: true,
  playsInline: true,
  "webkit-playsinline": "true",
  preload: "metadata",
  controlsList: "nodownload noremoteplayback noplaybackrate",
  onContextMenu: (e) => e.preventDefault(),
};

// Contenedor responsivo 16:9 fluido — funciona en teléfono vertical, horizontal y tablet.
const VIDEO_WRAPPER_CLS = "relative w-full max-w-full aspect-video rounded-lg overflow-hidden bg-black";
const VIDEO_INNER_CLS = "absolute inset-0 w-full h-full object-contain";

// Mapea la extensión al MIME type para <source>. iOS es estricto: sin type correcto puede fallar.
function videoMime(url) {
  const m = /\.(mp4|m4v|mov|webm|ogv)($|\?)/i.exec(url || "");
  if (!m) return "video/mp4";
  const ext = m[1].toLowerCase();
  if (ext === "webm") return "video/webm";
  if (ext === "ogv") return "video/ogg";
  return "video/mp4";  // .mp4, .m4v, .mov all served as video/mp4 (H.264/AAC)
}

// Aviso visible bajo cada video indicando que no se puede descargar.
function VideoProtectedNotice({ lang }) {
  return (
    <div
      className="flex items-center gap-2 text-[11.5px] text-tert mt-1.5 px-1"
      data-testid="video-notice-nodownload"
    >
      <IconShieldLock size={13} className="text-teal-deep" />
      <span>
        {lang === "es"
          ? "Video protegido · no se puede descargar."
          : "Protected video · downloads disabled."}
      </span>
    </div>
  );
}

export default function CourseView() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeLesson, setActiveLesson] = useState(null);
  const [certLoading, setCertLoading] = useState(false);
  const [sombraApproval, setSombraApproval] = useState(null); // { status, ... } | null

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/aula/courses/${courseId}`);
      setCourse(data);
      // If track=sombra and course is complete, fetch approval state
      if (data?.track === "sombra" && (data?.progress_pct || 0) === 100) {
        try {
          const { data: appr } = await api.get(`/aula/courses/${courseId}/sombra-approval-status`);
          setSombraApproval(appr);
        } catch { /* ignore */ }
      } else {
        setSombraApproval(null);
      }
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const completeLesson = async (lessonId) => {
    try { await api.post(`/aula/courses/${courseId}/complete-lesson`, { lesson_id: lessonId }); await load(); }
    catch (e) { setError(formatApiError(e)); }
  };

  // Colors used across the certificate (match brand identity)
  const CERT_COLORS = {
    teal_deep: [10, 63, 51],   // #0A3F33
    teal: [14, 91, 73],        // #0E5B49
    honey: [194, 135, 44],     // #C2872C
    honey_soft: [237, 210, 138],
    ink: [55, 65, 63],
    muted: [120, 120, 120],
  };

  const drawChiessaLogo = (doc, cx, cy, r) => {
    // Compass: outer ring + 3 spokes + 3 outer dots + honey center
    const [tr, tg, tb] = CERT_COLORS.teal_deep;
    const [hr, hg, hb] = CERT_COLORS.honey;
    doc.setDrawColor(tr, tg, tb);
    doc.setLineWidth(0.9);
    doc.circle(cx, cy, r, "S");
    // Top spoke
    doc.line(cx, cy, cx, cy - r * 0.9);
    // Bottom-right spoke
    doc.line(cx, cy, cx + r * 0.78, cy + r * 0.45);
    // Bottom-left spoke
    doc.line(cx, cy, cx - r * 0.78, cy + r * 0.45);
    // Outer dots
    doc.setFillColor(tr, tg, tb);
    doc.circle(cx, cy - r * 0.9, r * 0.14, "F");
    doc.circle(cx + r * 0.78, cy + r * 0.45, r * 0.14, "F");
    doc.circle(cx - r * 0.78, cy + r * 0.45, r * 0.14, "F");
    // Center
    doc.setFillColor(hr, hg, hb);
    doc.circle(cx, cy, r * 0.28, "F");
  };

  // Opens LinkedIn's "Add to profile" flow with course + verification metadata prefilled.
  const shareOnLinkedIn = async () => {
    if (!course) return;
    try {
      // Ensure we have a verification code / issued date (idempotent, doesn't re-fire emails)
      const { data: meta } = await api.post(`/aula/courses/${courseId}/certificate`);
      const issued = meta.issued_at ? new Date(meta.issued_at) : new Date();
      const params = new URLSearchParams({
        startTask: "CERTIFICATION_NAME",
        name: meta.course_title || course.title,
        organizationName: "Chiessa Institute",
        issueYear: String(issued.getFullYear()),
        issueMonth: String(issued.getMonth() + 1),
        certUrl: meta.verify_url,
        certId: meta.verification_code,
      });
      const url = `https://www.linkedin.com/profile/add?${params.toString()}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const downloadCertificate = async () => {
    if (!course) return;
    setError("");
    setCertLoading(true);
    try {
      // 1) Ask backend for cert metadata (issues + increments counter + fires emails)
      const { data: meta } = await api.post(`/aula/courses/${courseId}/certificate`);
      // 2) Generate the QR image (dataURL) → will be embedded in the PDF
      const qrDataUrl = await QRCode.toDataURL(meta.verify_url, {
        errorCorrectionLevel: "M",
        margin: 1,
        width: 260,
        color: { dark: "#0A3F33", light: "#F5EFDD" },
      });
      // 3) Preload Mariela's handwritten signature so jsPDF can embed it
      const sigImg = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = marielaSignature;
      });

      // 3) Build the PDF (A4 landscape · mm)
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const [tealR, tealG, tealB] = CERT_COLORS.teal_deep;
      const [tealMedR, tealMedG, tealMedB] = CERT_COLORS.teal;
      const [honeyR, honeyG, honeyB] = CERT_COLORS.honey;
      const [inkR, inkG, inkB] = CERT_COLORS.ink;
      const [mutedR, mutedG, mutedB] = CERT_COLORS.muted;

      // Ivory background
      doc.setFillColor(250, 244, 227);
      doc.rect(0, 0, W, H, "F");

      // Outer teal frame
      doc.setDrawColor(tealR, tealG, tealB);
      doc.setLineWidth(1.2);
      doc.rect(8, 8, W - 16, H - 16);
      // Inner honey frame (thin)
      doc.setDrawColor(honeyR, honeyG, honeyB);
      doc.setLineWidth(0.35);
      doc.rect(12, 12, W - 24, H - 24);
      // 4 honey corner dots
      doc.setFillColor(honeyR, honeyG, honeyB);
      [[10, 10], [W - 10, 10], [10, H - 10], [W - 10, H - 10]].forEach(([x, y]) => {
        doc.circle(x, y, 1.6, "F");
      });

      // Logo at top — visually centered above the header text
      drawChiessaLogo(doc, W / 2, 32, 11);

      // Brand name + tagline — centered without charSpace (jsPDF's align:"center"
      // doesn't factor in charSpace, so we drop it to keep everything aligned).
      doc.setFont("helvetica", "bold");
      doc.setTextColor(tealR, tealG, tealB);
      doc.setFontSize(17);
      doc.text("CHIESSA INSTITUTE", W / 2, 55, { align: "center" });

      doc.setFont("helvetica", "normal");
      doc.setTextColor(honeyR, honeyG, honeyB);
      doc.setFontSize(10);
      doc.text("NEUROEDUCACIÓN INCLUSIVA", W / 2, 61.5, { align: "center" });

      // Introductory line
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.setFontSize(11);
      doc.text("Otorga el presente diploma a", W / 2, 74, { align: "center" });

      // Participant name (big, bold, teal-deep, honey underline)
      const name = meta.user_name || user?.name || user?.email || "";
      doc.setFont("helvetica", "bold");
      doc.setTextColor(tealR, tealG, tealB);
      doc.setFontSize(30);
      doc.text(name, W / 2, 92, { align: "center" });
      // Honey underline
      const nameWidth = doc.getTextWidth(name);
      doc.setDrawColor(honeyR, honeyG, honeyB);
      doc.setLineWidth(0.6);
      doc.line(W / 2 - nameWidth / 2 - 5, 96, W / 2 + nameWidth / 2 + 5, 96);

      // Below the name: reason
      doc.setFont("helvetica", "normal");
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.setFontSize(11);
      doc.text("por haber completado satisfactoriamente el curso", W / 2, 106, { align: "center" });

      // Course title (bold italic-ish teal)
      doc.setFont("helvetica", "bolditalic");
      doc.setTextColor(tealMedR, tealMedG, tealMedB);
      doc.setFontSize(19);
      const titleLines = doc.splitTextToSize(meta.course_title || course.title, W - 80);
      doc.text(titleLines, W / 2, 118, { align: "center" });

      // Course meta line (modules · lessons · hours)
      const metaParts = [];
      if (meta.modules_count) metaParts.push(`${meta.modules_count} módulos`);
      if (meta.lessons_count) metaParts.push(`${meta.lessons_count} lecciones`);
      if (meta.hours) metaParts.push(`${meta.hours} horas`);
      if (metaParts.length) {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(honeyR, honeyG, honeyB);
        doc.setFontSize(9);
        const titleH = titleLines.length * 6;
        doc.text(metaParts.join(" · "), W / 2, 118 + titleH, { align: "center" });
      }

      // Quote
      doc.setFont("helvetica", "italic");
      doc.setTextColor(tealMedR, tealMedG, tealMedB);
      doc.setFontSize(11);
      doc.text(
        '"Que ningún niño quede fuera de la educación por la forma en que aprende."',
        W / 2,
        H - 55,
        { align: "center" },
      );

      // Signature (bottom left) — Mariela's handwritten signature above the line
      const sigCenterX = 45;
      const sigLineY = H - 32;
      // Scaled signature: max width 46mm, preserve aspect ratio
      const sigMaxW = 46;
      const sigAspect = sigImg.naturalHeight / sigImg.naturalWidth;
      const sigW = sigMaxW;
      const sigH = sigW * sigAspect;
      // Place so the ink sits naturally on top of (slightly crossing) the signature line
      const sigX = sigCenterX - sigW / 2;
      const sigY = sigLineY - sigH + 3; // 3mm overlap for a natural handwritten look
      doc.addImage(sigImg, "PNG", sigX, sigY, sigW, sigH);
      // Signature line
      doc.setDrawColor(inkR, inkG, inkB);
      doc.setLineWidth(0.4);
      doc.line(sigCenterX - 26, sigLineY, sigCenterX + 26, sigLineY);
      // Printed name + role
      doc.setFont("helvetica", "bold");
      doc.setTextColor(inkR, inkG, inkB);
      doc.setFontSize(11);
      doc.text("Dra. Mariela Chiessa", sigCenterX, sigLineY + 5, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.setFontSize(8.5);
      doc.text("Fundadora y Directora · Chiessa Institute", sigCenterX, sigLineY + 9.5, { align: "center" });

      // Date + city (bottom center)
      const issued = meta.issued_at ? new Date(meta.issued_at) : new Date();
      const monthYear = issued.toLocaleDateString("es-HN", { month: "long", year: "numeric" });
      const monthYearCap = monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.setFontSize(8.5);
      doc.text("FECHA DE EMISIÓN", W / 2, H - 36, { align: "center" });
      doc.setFontSize(11);
      doc.setTextColor(inkR, inkG, inkB);
      doc.text(monthYearCap, W / 2, H - 30, { align: "center" });
      doc.setFontSize(8.5);
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.text("La Ceiba, Honduras", W / 2, H - 25, { align: "center" });

      // QR (bottom right)
      const qrSize = 30;
      const qrX = W - qrSize - 25;
      const qrY = H - qrSize - 25;
      doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
      doc.setFontSize(7.5);
      doc.setTextColor(mutedR, mutedG, mutedB);
      doc.text("Escanea para verificar la autenticidad", qrX + qrSize / 2, qrY + qrSize + 3.5, { align: "center" });
      doc.setFont("helvetica", "bold");
      doc.setTextColor(tealR, tealG, tealB);
      doc.setFontSize(8);
      doc.text(`Código: ${meta.verification_code}`, qrX + qrSize / 2, qrY + qrSize + 7.5, { align: "center" });

      const safeTitle = (meta.course_title || "curso").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
      doc.save(`certificado_${safeTitle}.pdf`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCertLoading(false);
    }
  };

  const goCheckout = () => {
    nav(`/app/checkout/course/${courseId}`);
  };

  if (loading) return <div className="wrap-app py-10 text-tert font-mono text-[11px] uppercase">Cargando…</div>;
  if (!course) return <div className="wrap-app py-10">{error || "No encontrado"}</div>;

  const completedSet = new Set(course.completed_lesson_ids || []);
  const hasAccess = course.has_access !== false;
  const price = Number(course.price || 0);
  const isAdmin = user?.is_admin || user?.role === "psicologa";

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app/aula")} data-testid="course-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Aula" : "Aula"}
        </button>
        <span className="t">{lang === "es" ? "CURSO" : "COURSE"}</span>
      </div>
      <div className="wrap-app max-w-[860px] py-10">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="tag">{course.track}</span>
          <span className="tag tag-honey">{course.level}</span>
          {course.is_capsule && <span className="tag" style={{ background: "#EEEDFE", color: "#3C3489" }}>Cápsula</span>}
          {!hasAccess && <span className="tag" style={{ background: "#F3EDE1", color: "#854F0B" }}><IconLock size={10} className="inline mr-0.5" /> {lang === "es" ? "Bloqueado" : "Locked"}</span>}
          {isAdmin && (
            <button className="btn-ghost btn text-[11.5px] ml-auto" onClick={() => nav(`/app/aula/edit/${courseId}`)} data-testid="course-edit">
              {lang === "es" ? "Editar" : "Edit"}
            </button>
          )}
        </div>
        <h1 className="font-display text-[28px] font-medium leading-tight">{course.title}</h1>
        {course.subtitle && <p className="text-muted text-[15px] mt-1">{course.subtitle}</p>}
        <p className="text-[14px] mt-4 leading-relaxed">{course.description}</p>

        <div className="flex items-center gap-4 mt-4 text-tert text-[12px]">
          <span className="flex items-center gap-1"><IconClock size={13} /> {course.duration_min} min</span>
          <span>· {course.total_lessons} {lang === "es" ? "lecciones" : "lessons"}</span>
        </div>

        {!hasAccess && (
          <div className="card mt-6 border-l-[4px] border-l-honey" data-testid="course-paywall">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <p className="lbl m-0">{lang === "es" ? "Curso premium" : "Premium course"}</p>
                <h3 className="font-display text-[22px] font-medium mt-1">
                  {lang === "es" ? "Compra este curso" : "Buy this course"}
                </h3>
                <p className="text-muted text-[13px] mt-2">
                  {lang === "es"
                    ? "Al comprar, tendrás acceso permanente al contenido de todas las lecciones + certificado."
                    : "Purchase for permanent access to all lessons + certificate."}
                </p>
              </div>
              <div className="text-right">
                <p className="font-display text-[32px] font-medium text-teal-deep">${price.toFixed(2)}</p>
                <p className="text-tert text-[11px]">USD · {lang === "es" ? "pago único" : "one-time"}</p>
                <div className="flex flex-col gap-1.5 mt-3 items-stretch">
                  <button className="btn text-[13px]" onClick={goCheckout} data-testid="course-buy">
                    <IconShoppingCart size={13} />
                    {lang === "es" ? "Comprar" : "Buy"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasAccess && (
          <>
            <div className="track mt-4"><div className="fill" style={{ width: `${course.progress_pct || 0}%` }} /></div>
            <p className="text-[12px] text-teal mt-1">{course.progress_pct || 0}% {lang === "es" ? "completo" : "complete"}</p>
            {course.progress_pct === 100 && course.track === "sombra" && sombraApproval?.status !== "approved" && (
              <div
                className="mt-4 rounded-lg border border-honey/50 bg-honey/10 p-4 flex items-start gap-3 flex-wrap"
                data-testid="course-sombra-pending-panel"
              >
                <IconAward size={28} className="text-honey shrink-0" />
                <div className="flex-1 min-w-[240px]">
                  <p className="lbl m-0 text-teal-deep">{lang === "es" ? "¡Curso completado!" : "Course completed!"}</p>
                  <h3 className="font-display text-[19px] font-medium mt-1">
                    {lang === "es" ? "Entrevista pendiente con Dra. Mariela Chiessa" : "Interview with Dra. Mariela Chiessa pending"}
                  </h3>
                  <p className="text-muted text-[12.5px] mt-1 leading-relaxed">
                    {lang === "es"
                      ? "Como Maestra Sombra, tu certificado se entrega después de una entrevista directa con la Dra. Mariela. Te contactará por correo para coordinar la fecha. Este paso valida tu preparación integral antes de emitir tu certificación profesional."
                      : "As a Shadow Teacher, your certificate is issued after a direct interview with Dra. Mariela. She will reach out by email to coordinate. This step validates your integral preparation before issuing your professional certification."}
                  </p>
                  {sombraApproval?.status === "pending_interview" && sombraApproval?.requested_at && (
                    <p className="text-tert text-[11.5px] mt-2 font-mono">
                      {lang === "es" ? "Solicitud registrada · " : "Request logged · "}
                      {new Date(sombraApproval.requested_at).toLocaleDateString(lang === "es" ? "es-HN" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
                    </p>
                  )}
                </div>
              </div>
            )}
            {course.progress_pct === 100 && (course.track !== "sombra" || sombraApproval?.status === "approved") && (
              <div
                className="mt-4 rounded-lg border border-honey/50 bg-honey/10 p-4 flex items-start gap-3 flex-wrap"
                data-testid="course-cert-panel"
              >
                <IconAward size={28} className="text-honey shrink-0" />
                <div className="flex-1 min-w-[240px]">
                  <p className="lbl m-0 text-teal-deep">{lang === "es" ? "¡Curso completado!" : "Course completed!"}</p>
                  <h3 className="font-display text-[19px] font-medium mt-1">
                    {lang === "es" ? "Certificado disponible" : "Certificate available"}
                  </h3>
                  <p className="text-muted text-[12.5px] mt-1">
                    {lang === "es"
                      ? "Descarga tu certificado personalizado en PDF con tu nombre, código único de verificación y QR."
                      : "Download your personalized PDF certificate with your name, unique verification code and QR."}
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                  <button
                    className="btn text-[12.5px]"
                    onClick={downloadCertificate}
                    disabled={certLoading}
                    data-testid="course-cert-download"
                  >
                    {certLoading ? <IconLoader2 size={13} className="animate-spin" /> : <IconDownload size={13} />}
                    {lang === "es" ? "Descargar certificado" : "Download certificate"}
                  </button>
                  <button
                    className="btn-ghost btn text-[12.5px]"
                    onClick={shareOnLinkedIn}
                    data-testid="course-cert-linkedin"
                    title={lang === "es" ? "Agregar a tu perfil de LinkedIn" : "Add to your LinkedIn profile"}
                  >
                    <span style={{ background: "#0A66C2", color: "#fff", fontWeight: 700, borderRadius: 3, padding: "2px 5px", fontSize: 10, lineHeight: 1 }}>in</span>
                    {lang === "es" ? "Compartir en LinkedIn" : "Share on LinkedIn"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="mt-8 space-y-6">
          {course.modules.map((m, mi) => (
            <div key={m.id}>
              <p className="lbl">
                {lang === "es" ? "Módulo" : "Module"} {String(mi + 1).padStart(2, "0")} · {m.title}
              </p>
              <div className="space-y-2 mt-2">
                {m.lessons.map((l, li) => {
                  const done = completedSet.has(l.id);
                  const isActive = activeLesson === l.id;
                  const locked = l.locked === true;
                  return (
                    <motion.div key={l.id} className={`card ${locked ? "opacity-70" : ""}`} data-testid={`lesson-${l.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-medium ${done ? "bg-oktext text-white" : locked ? "bg-line text-tert" : "bg-teal-soft text-teal-deep"}`}>
                            {done ? <IconCheck size={14} /> : locked ? <IconLock size={12} /> : String(li + 1).padStart(2, "0")}
                          </span>
                          <div className="flex-1">
                            <p className="text-[14px] font-medium flex items-center gap-2">
                              {TYPE_ICON[l.type] || null} {l.title}
                            </p>
                            <p className="text-tert text-[11.5px]">{l.duration_min} min · {l.type}</p>
                          </div>
                        </div>
                        {!locked && (
                          <div className="flex gap-2">
                            <button
                              className="btn-ghost btn text-[11.5px] px-2 py-1"
                              onClick={() => setActiveLesson(isActive ? null : l.id)}
                              data-testid={`lesson-open-${l.id}`}
                            >
                              {isActive ? (lang === "es" ? "Cerrar" : "Close") : (lang === "es" ? "Abrir" : "Open")}
                            </button>
                            {!done && (
                              <button
                                className="btn text-[11.5px] px-2 py-1"
                                onClick={() => completeLesson(l.id)}
                                data-testid={`lesson-complete-${l.id}`}
                              >
                                <IconCheck size={12} /> {lang === "es" ? "Marcar" : "Mark"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {isActive && !locked && (
                        <div className="mt-4 pt-4 border-t border-line text-[13.5px] leading-relaxed space-y-3">
                          {/* Video: URL externa preferida */}
                          {l.video_url && (
                            <>
                              {isDirectVideoUrl(l.video_url) ? (
                                <div className={VIDEO_WRAPPER_CLS}>
                                  <video
                                    {...PROTECTED_VIDEO_PROPS}
                                    className={VIDEO_INNER_CLS}
                                    data-testid={`lesson-video-direct-${l.id}`}
                                  >
                                    <source src={l.video_url} type={videoMime(l.video_url)} />
                                  </video>
                                </div>
                              ) : (
                                <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
                                  <iframe
                                    src={toEmbed(l.video_url)}
                                    title={l.title}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    className="w-full h-full"
                                    data-testid={`lesson-video-external-${l.id}`}
                                  />
                                  {isDriveEmbed(l.video_url) && (
                                    <div
                                      className="absolute top-0 right-0 w-14 h-14 pointer-events-auto"
                                      style={{ background: "transparent" }}
                                      aria-hidden="true"
                                      data-testid={`drive-popout-mask-${l.id}`}
                                      onClick={(e) => e.preventDefault()}
                                    />
                                  )}
                                </div>
                              )}
                              <VideoProtectedNotice lang={lang} />
                            </>
                          )}
                          {/* Video: archivo subido (video_file nuevo o legacy file_id con type=video) */}
                          {!l.video_url && l.video_file?.file_id && (
                            <>
                              <div className={VIDEO_WRAPPER_CLS}>
                                <video
                                  {...PROTECTED_VIDEO_PROPS}
                                  className={VIDEO_INNER_CLS}
                                  data-testid={`lesson-video-file-${l.id}`}
                                >
                                  <source src={`${API}/aula/file/${l.video_file.file_id}`} type="video/mp4" />
                                </video>
                              </div>
                              <VideoProtectedNotice lang={lang} />
                            </>
                          )}
                          {!l.video_url && !l.video_file && l.type === "video" && l.file_id && (
                            <>
                              <div className={VIDEO_WRAPPER_CLS}>
                                <video
                                  {...PROTECTED_VIDEO_PROPS}
                                  className={VIDEO_INNER_CLS}
                                  data-testid={`lesson-video-legacy-${l.id}`}
                                >
                                  <source src={`${API}/aula/file/${l.file_id}`} type="video/mp4" />
                                </video>
                              </div>
                              <VideoProtectedNotice lang={lang} />
                            </>
                          )}
                          {/* Documentos PDF (nuevo modelo — hasta 2) */}
                          {Array.isArray(l.documents) && l.documents.length > 0 && (
                            <div className="space-y-1.5" data-testid={`lesson-documents-${l.id}`}>
                              {l.documents.map((d, di) => (
                                <a
                                  key={di}
                                  href={`${API}/aula/file/${d.file_id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn-ghost btn text-[12.5px] inline-flex w-full sm:w-auto sm:mr-2"
                                  data-testid={`lesson-doc-${l.id}-${di}`}
                                >
                                  <IconDownload size={12} /> {d.file_name || (lang === "es" ? "Documento" : "Document")}
                                  <IconExternalLink size={11} className="opacity-60" />
                                </a>
                              ))}
                            </div>
                          )}
                          {/* Legacy document (curso viejo con file_id + type != video) */}
                          {(!l.documents || l.documents.length === 0) && l.type !== "video" && l.file_id && (
                            <a
                              href={`${API}/aula/file/${l.file_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-ghost btn text-[12.5px] inline-flex"
                              data-testid={`lesson-file-download-${l.id}`}
                            >
                              <IconDownload size={12} /> {l.file_name || (lang === "es" ? "Abrir documento" : "Open document")}
                              <IconExternalLink size={11} className="opacity-60" />
                            </a>
                          )}
                          {l.body && <div className="text-muted whitespace-pre-wrap">{l.body}</div>}
                          {!l.file_id && !l.video_url && !l.video_file && (!l.documents || l.documents.length === 0) && !l.body && (
                            <span className="text-tert italic">
                              {lang === "es"
                                ? "Contenido pendiente. Marca como completada cuando termines."
                                : "Content pending. Mark complete when done."}
                            </span>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {error && <p className="text-errtext text-[13px] mt-4">{error}</p>}
      </div>
    </>
  );
}
